import { useCallback, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

import { FORM_TYPE_CREATE_DIRECT_FLOW } from 'js/constants/formTypes';
import { shouldRenderRunAction } from 'js/components/pipelines/cards/components/ExpandActions/utils/shouldRenderSourceAction';
import { useChangeDetection } from './useChangeDetection';
import { useForm } from 'new/elements/Form/useForm';

export const useDirectFlowSaveConfirmation = () => {
  // SaveConfirmation modal state
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const pendingSaveRef = useRef<((runWithIngestion: boolean) => Promise<boolean>) | null>(null);

  // Get the current runWithIngestion value from form data
  const formData = useSelector((state: any) => state.forms[FORM_TYPE_CREATE_DIRECT_FLOW]?.data);
  const currentRunWithIngestion = formData?.runWithIngestion || false;

  // Get change detection for the form
  const { hasChanges } = useChangeDetection();

  const { setShowLeaveConfirmation, overrideNextFunction, cancelValidation } = useForm();

  const shouldShowSaveConfirmation = useCallback(
    (source: any, connectionCategory: string, isSaving: boolean) => {
      const result = !!(
        source && // We have a source object
        source?.status === 'ACTIVE' && // Source is active
        shouldRenderRunAction(source, connectionCategory) &&
        hasChanges && // Only show confirmation if there are actual changes
        !isSaving
      );

      return result;
    },
    [hasChanges],
  );

  const handleSaveConfirmation = useCallback(
    async (runWithIngestion: boolean) => {
      setIsSaving(true);
      setShowSaveConfirmation(false);

      // Call the pending save function if it exists
      let result;
      if (pendingSaveRef.current) {
        result = await pendingSaveRef.current(runWithIngestion);
        pendingSaveRef.current = null;
      } else {
        result = false;
      }
      setIsSaving(false);

      if (result) {
        setShowLeaveConfirmation(false);

        return true;
      }

      return false;
    },
    [setShowLeaveConfirmation],
  );

  const onCloseSaveConfirmation = useCallback(() => {
    setShowSaveConfirmation(false);
    cancelValidation(); // Stop the loading indicator
    pendingSaveRef.current = null;
  }, [cancelValidation]);

  return {
    showSaveConfirmation,
    setShowSaveConfirmation,
    isSaving,
    setIsSaving,
    pendingSaveRef,
    currentRunWithIngestion,
    hasChanges,
    shouldShowSaveConfirmation,
    handleSaveConfirmation,
    onCloseSaveConfirmation,
    setShowLeaveConfirmation,
    overrideNextFunction,
  };
};
